import React, { ChangeEvent, FocusEvent, useContext } from 'react';

import { observer } from 'mobx-react-lite';

import { notify } from 'components/common/Toast/toast.helpers';
import { RootStoreContext } from 'index';
import TextInput from 'components/common/TextInput/TextInput';
import Button from 'components/common/Button/Button';
import useForm from 'hooks/useForm';
import { validateEmail } from 'components/auth/authValidation.utils';
import { InviteCustomerFieldName } from 'components/applications/ApplicationsPage/InviteCustomerForm/inviteCustomerForm.enums';
import { INVITATION_SUCCESS_MESSAGE } from 'components/applications/ApplicationsPage/InviteCustomerForm/inviteCustomerForm.constants';
import { validateFirstName, validateRequiredField } from 'common/utils/validation.utils';
import { ErrorMessage } from 'common/enums/errorMessages.enums';

import styles from './InviteCustomerForm.module.scss';

interface InviteCustomerSideBarProps {
  onClose: () => void;
}

const InviteCustomerForm = ({ onClose }: InviteCustomerSideBarProps) => {
  const { intermediaryStore } = useContext(RootStoreContext);
  const { errors, values, setValue, validateField, handleSubmit } = useForm({
    defaultValues: {
      [InviteCustomerFieldName.FirstName]: '',
      [InviteCustomerFieldName.Email]: '',
    },
    handleValidation: (fieldName, value) => {
      switch (fieldName) {
        case InviteCustomerFieldName.FirstName: {
          return validateRequiredField(value, fieldName);
        }
        case InviteCustomerFieldName.Email: {
          return validateEmail(value, fieldName);
        }
      }

      return '';
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    setValue(name, value);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as keyof typeof values;

    validateField(name, values[name]);
  };

  const handleInvitationSubmit = handleSubmit(async () => {
    try {
      await intermediaryStore.inviteCustomer({
        firstName: values[InviteCustomerFieldName.FirstName],
        email: values[InviteCustomerFieldName.Email],
      });
      onClose();

      notify(INVITATION_SUCCESS_MESSAGE, 'success');
    } catch (error: any) {
      notify(ErrorMessage.UnknownError, 'error');
    }
  });

  return (
    <form className={styles.inputContainer}>
      <TextInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={InviteCustomerFieldName.FirstName}
        name={InviteCustomerFieldName.FirstName}
        tabIndex={1}
        errorMessage={errors[InviteCustomerFieldName.FirstName]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <TextInput
        className={styles.input}
        containerClassName={styles.inputContainer}
        labelTitle={InviteCustomerFieldName.Email}
        name={InviteCustomerFieldName.Email}
        tabIndex={2}
        errorMessage={errors[InviteCustomerFieldName.Email]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      <Button
        className={styles.button}
        onClick={handleInvitationSubmit}
        isLoading={intermediaryStore.isInviteCustomerInProgress}
      >
        Send Message
      </Button>
    </form>
  );
};

export default observer(InviteCustomerForm);
